/* AuthProviderSucceeded / AuthProviderFailed / AuthCompleted.
 *
 * These three exist in the Amplitude tracking plan and are emitted today by
 * platform-auth-web-app (`app: "authWebApp"`), but they are NOT in this repo's
 * generated client: `src/integrations/ampli/base` was pulled at plan version
 * 4.0.0, which predates them. `ampli pull` needs an interactive Amplitude
 * login, so they are declared here — outside the generated directory, which a
 * pull would overwrite — and sent through `ampli.track()`, the generated
 * client's own escape hatch for events it has no typed method for.
 *
 * The property shapes below are taken from the live event schemas in
 * `language-studio.amplitude_events.*` rather than guessed, so what this app
 * sends is byte-compatible with what authWebApp already sends and the two land
 * in one funnel.
 *
 * When the plan is next pulled these become generated methods and this file
 * should be deleted in favour of `ampli.authCompleted(...)` etc. */

// BaseEvent is re-exported by the generated client (it aliases
// amplitude.Types.BaseEvent), so these ride the same type as every generated
// event without adding a dependency.
import type { BaseEvent } from "./base/index";

/** The `app` enum from the generated AuthStartedProperties, narrowed to the
 *  value this repo sends. Keeping it a literal means a rename in the plan
 *  fails the build here rather than silently splitting the funnel. */
type App = "tokenwise";

/** Shared by all three: the same context the generated AuthStarted carries, so
 *  a funnel can be built on (app, authMethod, authType) across the four. */
interface AuthEventProperties {
  app: App;
  authMethod?: string;
  authType?: string;
  origin?: string;
  page?: string;
  path?: string;
}

/** AuthProviderFailed additionally carries the failure detail, matching the
 *  live schema (errorCode / errorMessage). */
export interface AuthProviderFailedProperties extends AuthEventProperties {
  errorCode?: string;
  errorMessage?: string;
}

export class AuthProviderSucceeded implements BaseEvent {
  event_type = "AuthProviderSucceeded";

  constructor(public event_properties: AuthEventProperties) {
    this.event_properties = event_properties;
  }
}

export class AuthProviderFailed implements BaseEvent {
  event_type = "AuthProviderFailed";

  constructor(public event_properties: AuthProviderFailedProperties) {
    this.event_properties = event_properties;
  }
}

export class AuthCompleted implements BaseEvent {
  event_type = "AuthCompleted";

  constructor(public event_properties: AuthEventProperties) {
    this.event_properties = event_properties;
  }
}

export type { AuthEventProperties };
